import React, { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const STRATEGY_TAGS = [
  'Breakout',
  'Pullback',
  'Support Bounce',
  'Resistance Rejection',
  'Trend Continuation',
  'Mean Reversion',
  'RSI Oversold',
  'MACD Crossover',
  'Volume Breakout',
];

const PRE_TRADE_CHECKLIST = [
  { id: 'trend', label: 'Trend confirmed' },
  { id: 'level', label: 'Support or resistance identified' },
  { id: 'volume', label: 'Volume confirmation' },
  { id: 'risk', label: 'Risk reward checked' },
  { id: 'stoploss', label: 'Stoploss placed' },
];

function TradeJournalModal({ isOpen, onClose, onSubmit, orderDetails }) {
  const { colors } = useTheme();
  const [selectedTags, setSelectedTags] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const toggleChecklistItem = (id) => {
    setChecklist(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = () => {
    onSubmit({
      strategy_tags: selectedTags,
      analysis_checklist: checklist,
      notes,
    });
    onClose();
  };

  const allChecked = PRE_TRADE_CHECKLIST.every(item => checklist[item.id]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
    >
      <div
        className="app-card w-full max-w-2xl rounded-t-2xl p-4 max-h-[90vh] overflow-y-auto"
        style={{
          background: `linear-gradient(135deg, ${colors.accent}40 0%, ${colors.accent}15 100%), ${colors.cardBg}`,
          borderTop: `2px solid ${colors.accentBorder}`,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-bold" style={{ color: colors.primaryText }}>
            Trade Journal
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full"
            style={{ backgroundColor: colors.inputBg }}
          >
            <X className="w-5 h-5" style={{ color: colors.secondaryText }} />
          </button>
        </div>

        {/* Order Summary */}
        <div
          className="p-3 rounded-lg mb-4"
          style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}
        >
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span style={{ color: colors.secondaryText }}>Stock:</span>
              <span className="ml-2 font-semibold" style={{ color: colors.primaryText }}>
                {orderDetails?.symbol}
              </span>
            </div>
            <div>
              <span style={{ color: colors.secondaryText }}>Type:</span>
              <span className="ml-2 font-semibold" style={{ color: colors.primaryText }}>
                {orderDetails?.order_type}
              </span>
            </div>
            <div>
              <span style={{ color: colors.secondaryText }}>Quantity:</span>
              <span className="ml-2 font-semibold" style={{ color: colors.primaryText }}>
                {orderDetails?.quantity}
              </span>
            </div>
            <div>
              <span style={{ color: colors.secondaryText }}>Price:</span>
              <span className="ml-2 font-semibold" style={{ color: colors.primaryText }}>
                ₹{orderDetails?.price}
              </span>
            </div>
          </div>
        </div>

        {/* Pre-trade Checklist */}
        <div className="mb-4">
          <h3 className="text-sm font-semibold mb-3" style={{ color: colors.secondaryText }}>
            Pre-trade Analysis
          </h3>
          <div className="space-y-2">
            {PRE_TRADE_CHECKLIST.map((item) => (
              <label
                key={item.id}
                className="flex items-center p-3 rounded-lg cursor-pointer"
                style={{
                  backgroundColor: checklist[item.id] ? `${colors.accent}15` : colors.inputBg,
                  borderColor: checklist[item.id] ? colors.accent : colors.border,
                  borderWidth: '1px',
                }}
              >
                <input
                  type="checkbox"
                  checked={checklist[item.id] || false}
                  onChange={() => toggleChecklistItem(item.id)}
                  className="sr-only"
                />
                <div
                  className="w-5 h-5 rounded flex items-center justify-center mr-3"
                  style={{
                    backgroundColor: checklist[item.id] ? colors.accent : 'transparent',
                    borderWidth: checklist[item.id] ? '0' : '2px',
                    borderColor: colors.border,
                  }}
                >
                  {checklist[item.id] && (
                    <CheckCircle className="w-4 h-4 text-white" />
                  )}
                </div>
                <span style={{ color: colors.primaryText }}>{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Strategy Tags */}
        <div className="mb-4">
          <h3 className="text-sm font-semibold mb-3" style={{ color: colors.secondaryText }}>
            Strategy Tags
          </h3>
          <div className="flex flex-wrap gap-2">
            {STRATEGY_TAGS.map((tag) => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className="px-3 py-1.5 rounded-full text-[12px] font-medium transition-all"
                style={{
                  backgroundColor: selectedTags.includes(tag) ? colors.accent : colors.inputBg,
                  color: selectedTags.includes(tag) ? '#ffffff' : colors.primaryText,
                  borderWidth: '1px',
                  borderColor: selectedTags.includes(tag) ? colors.accent : colors.border,
                }}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2" style={{ color: colors.secondaryText }}>
            Additional Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional observations..."
            rows={4}
            className="w-full p-3 rounded-lg resize-none"
            style={{
              backgroundColor: colors.inputBg,
              borderWidth: '1px',
              borderColor: colors.border,
              color: colors.primaryText,
            }}
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!allChecked || selectedTags.length === 0}
          className="w-full py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50"
          style={{
            backgroundColor: colors.accent,
          }}
        >
          {!allChecked
            ? 'Complete Checklist to Continue'
            : selectedTags.length === 0
            ? 'Select at least one strategy tag'
            : 'Submit Trade Journal'}
        </button>
      </div>
    </div>
  );
}

export default TradeJournalModal;
